import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Container,
  Stack,
  AppBar,
  Toolbar,
  Typography,
  Dialog,
  IconButton,
  TableContainer,
  Table,
  TableCell,
  TableRow,
  TableHead,
  TableBody,
  Divider,
  Box,
  Tabs,
  Tab,
} from "@mui/material";
import { isEmpty } from "lodash";
import { useParams } from "react-router-dom";
import ReactToPrint from "react-to-print";
import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCloudArrowDown,
  faMapLocationDot,
  faListOl,
} from "@fortawesome/free-solid-svg-icons";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Tooltip as EtiquetaMapa,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import moment from "moment";
import * as XLSX from "xlsx/xlsx.mjs";

import { DataContext } from "../../../context/DataContext";
import { getRol, getToken, getUser } from "../../../api/Account";
import { getMoraPorZonaAsync } from "../../../api/Ruta";
import {
  getGestoresAsync,
  getGestoresBySucursalAccessAsync,
  getSucursalByUserAsync,
} from "../../../api/Users";
import { simpleMessage } from "../../../helpers/Helpers";
import { EncabezadoOrdenable, useOrdenTabla } from "../../../helpers/OrdenTabla";
import { AjustarVista, icono } from "../../../components/MapaSeguimiento";
import { CENTRO_POR_DEFECTO } from "../../../helpers/Seguimiento";
import NoData from "../../../components/NoData";
import { PrintReport } from "../../../components/PrintReport";

const moneda = (valor) =>
  new Intl.NumberFormat("es-NI", { style: "currency", currency: "NIO" }).format(
    valor
  );

/**
 * Cortes del semáforo, en porcentaje del saldo del barrio que está vencido y sin pagar.
 * Son de negocio y discutibles; el reporte muestra siempre el porcentaje al lado, así que
 * nadie tiene que creerle al color.
 */
const MORA_ALTA = 20;
const MORA_MEDIA = 10;

const colorDe = (porcentaje) =>
  porcentaje >= MORA_ALTA ? "#d32f2f" : porcentaje >= MORA_MEDIA ? "#ed6c02" : "#00a152";

const MoraPorZona = () => {
  const { selectedSucursal, selectedGestor } = useParams();
  const [reporte, setReporte] = useState(null);
  const [sucursalName, setSucursalName] = useState("");
  const [gestorName, setGestorName] = useState("");
  const [vista, setVista] = useState("lista");

  const compRef = useRef();
  const { setIsLoading, setIsUnautorized, title, slogan, icon } =
    useContext(DataContext);

  const token = getToken();
  const user = getUser();
  const rol = getRol();

  const { orden, cambiarOrden, ordenar } = useOrdenTabla("porcentajeMora", [
    "barrio",
  ]);

  useEffect(() => {
    (async () => {
      await getSucursals();
      await getGestores();

      setIsLoading(true);
      const result = await getMoraPorZonaAsync(token, {
        sucursalId: selectedSucursal === "t" ? 0 : Number(selectedSucursal),
        gestorId: selectedGestor === "t" ? 0 : Number(selectedGestor),
      });
      setIsLoading(false);

      if (!result.statusResponse) {
        if (result.error === "Unauthorized") {
          setIsUnautorized(true);
          return;
        }
        simpleMessage(result.error, "error");
        return;
      }

      setReporte(result.data.result);
    })();
  }, []);

  const getSucursals = async () => {
    const result = await getSucursalByUserAsync(token);
    if (!result.statusResponse) {
      return;
    }
    const encontrada = result.data.result.filter(
      (s) => s.id.toString() === selectedSucursal
    );
    setSucursalName(isEmpty(encontrada) ? "" : encontrada[0].description);
  };

  const getGestores = async () => {
    const result =
      selectedSucursal === "t"
        ? await getGestoresBySucursalAccessAsync(token)
        : await getGestoresAsync(token, selectedSucursal);
    if (!result.statusResponse) {
      return;
    }
    const encontrado = result.data.result.filter(
      (g) => g.id.toString() === selectedGestor
    );
    setGestorName(isEmpty(encontrado) ? "" : encontrado[0].fullName);
  };

  const pageStyle = `
  @media print {
    html, body {
      height: initial !important;
      overflow: initial !important;
      -webkit-print-color-adjust: exact;
    }
  }
  @media all {
    .pagebreak {
      display: none;
    }
  }
  @media print {
    .pagebreak {
      page-break-before: always;
    }
  }
  @page { size: landscape;  margin: 4mm; }
`;

  if (reporte === null) {
    return null;
  }

  const zonas = reporte.zonas ?? [];
  const filas = ordenar(zonas);
  const enMapa = zonas.filter((z) => z.latitude !== null);
  const posiciones = enMapa.map((z) => [z.latitude, z.longitude]);

  const exportExcel = () => {
    let exp = [];

    filas.forEach((z) => {
      exp.push({
        barrio: z.barrio,
        creditos: z.creditos,
        creditosEnMora: z.creditosEnMora,
        saldoTotal: z.saldoTotal,
        montoEnMora: z.montoEnMora,
        porcentajeMora: z.porcentajeMora,
        diasMoraPromedio: z.diasMoraPromedio,
        clientesUbicados: z.conUbicacion,
      });
    });

    var bk = XLSX.utils.book_new(),
      bs = XLSX.utils.json_to_sheet(exp);

    XLSX.utils.book_append_sheet(bk, bs, "moraPorZona");
    XLSX.writeFile(bk, "MoraPorZona.xlsx");
  };

  const t = (compacto, chico, grande) => (compacto ? chico : grande);

  const parametros = (compacto) => (
    <Stack direction={"row"} justifyContent="space-between">
      <Stack>
        <Typography
          sx={{ color: "text.secondary", textAlign: "left" }}
          fontSize={t(compacto, 11, 13)}
        >
          Parametros:
        </Typography>
        <Typography fontSize={t(compacto, 11, 14)} textAlign="left">
          {`Sucursal: ${selectedSucursal === "t" ? "Todas" : sucursalName}`}
        </Typography>
        <Typography fontSize={t(compacto, 11, 14)} textAlign="left">
          {`Gestor Seleccionado: ${selectedGestor === "t" ? "Todos" : gestorName}`}
        </Typography>
        <Typography fontSize={t(compacto, 11, 14)} textAlign="left">
          Créditos activos y entregados
        </Typography>
      </Stack>

      <Stack>
        <Typography
          sx={{ color: "text.secondary", textAlign: "left" }}
          fontSize={t(compacto, 11, 13)}
        >
          Usuario:
        </Typography>
        <Typography fontSize={t(compacto, 11, 14)} textAlign="left">
          {user}
        </Typography>
        <Typography fontSize={t(compacto, 11, 14)} textAlign="left">
          {rol}
        </Typography>
        <Typography fontSize={t(compacto, 11, 14)} textAlign="left">
          {`Fecha y Hora: ${moment().format("DD/MM/YYYY hh:mm A")}`}
        </Typography>
      </Stack>
    </Stack>
  );

  const totales = (compacto) => {
    const tamano = t(compacto, 11, 14);

    const Total = ({ titulo, valor, color }) => (
      <Stack textAlign="center">
        <Typography
          style={{ fontWeight: "bold", color: color ?? "#03a9f4", fontSize: tamano }}
        >
          {titulo}
        </Typography>
        <Typography style={{ fontSize: tamano }}>{valor}</Typography>
      </Stack>
    );

    return (
      <Stack direction="row" flex="row" justifyContent="space-around">
        <Total titulo="Barrios" valor={zonas.length} />
        <Total
          titulo="Créditos"
          valor={zonas.reduce((s, z) => s + z.creditos, 0)}
        />
        <Total titulo="Total Saldo" valor={moneda(reporte.saldoTotal)} />
        <Total
          titulo="Total en Mora"
          valor={moneda(reporte.montoEnMora)}
          color={colorDe(reporte.porcentajeMora)}
        />
        <Total
          titulo="Mora de la Cartera"
          valor={`${reporte.porcentajeMora}%`}
          color={colorDe(reporte.porcentajeMora)}
        />
      </Stack>
    );
  };

  const tabla = (compacto) => {
    const th = { fontSize: t(compacto, 9, 14) };
    const td = { fontSize: t(compacto, 8, 13) };

    return (
      <TableContainer>
        <Table aria-label="simple table" size="small">
          <TableHead>
            <TableRow>
              <EncabezadoOrdenable campo="barrio" orden={orden} cambiarOrden={cambiarOrden} align="left" className="text-primary fw-bold" style={th}>
                Barrio
              </EncabezadoOrdenable>
              <EncabezadoOrdenable campo="creditos" orden={orden} cambiarOrden={cambiarOrden} align="center" className="text-primary fw-bold" style={th}>
                Créditos
              </EncabezadoOrdenable>
              <EncabezadoOrdenable campo="creditosEnMora" orden={orden} cambiarOrden={cambiarOrden} align="center" className="text-primary fw-bold" style={th}>
                En Mora
              </EncabezadoOrdenable>
              <EncabezadoOrdenable campo="saldoTotal" orden={orden} cambiarOrden={cambiarOrden} align="center" className="text-primary fw-bold" style={th}>
                Saldo Total
              </EncabezadoOrdenable>
              <EncabezadoOrdenable campo="montoEnMora" orden={orden} cambiarOrden={cambiarOrden} align="center" className="text-primary fw-bold" style={th}>
                Monto en Mora
              </EncabezadoOrdenable>
              <EncabezadoOrdenable campo="porcentajeMora" orden={orden} cambiarOrden={cambiarOrden} align="center" className="text-primary fw-bold" style={th}>
                % Mora
              </EncabezadoOrdenable>
              <EncabezadoOrdenable campo="diasMoraPromedio" orden={orden} cambiarOrden={cambiarOrden} align="center" className="text-primary fw-bold" style={th}>
                D.Mora Prom.
              </EncabezadoOrdenable>
              <EncabezadoOrdenable campo="conUbicacion" orden={orden} cambiarOrden={cambiarOrden} align="center" className="text-primary fw-bold" style={th}>
                Ubicados
              </EncabezadoOrdenable>
            </TableRow>
          </TableHead>
          <TableBody>
            {filas.map((z) => (
              <TableRow key={z.barrio}>
                <TableCell align="left" component="th" scope="row" style={td}>
                  {z.barrio}
                </TableCell>
                <TableCell align="center" scope="row" style={td}>
                  {z.creditos}
                </TableCell>
                <TableCell align="center" scope="row" style={td}>
                  {z.creditosEnMora}
                </TableCell>
                <TableCell align="center" scope="row" style={td}>
                  {moneda(z.saldoTotal)}
                </TableCell>
                <TableCell align="center" scope="row" style={td}>
                  {moneda(z.montoEnMora)}
                </TableCell>
                <TableCell
                  align="center"
                  scope="row"
                  style={{ ...td, fontWeight: "bold", color: colorDe(z.porcentajeMora) }}
                >
                  {`${z.porcentajeMora}%`}
                </TableCell>
                <TableCell align="center" scope="row" style={td}>
                  {z.diasMoraPromedio}
                </TableCell>
                <TableCell align="center" scope="row" style={td}>
                  {`${z.conUbicacion} de ${z.creditos}`}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const leyenda = (compacto) => {
    const chico = t(compacto, 8, 12);

    return (
      <>
        <Divider sx={{ marginY: "10px" }} />
        <Typography
          fontSize={t(compacto, 9, 13)}
          fontWeight="bold"
          textAlign="left"
          sx={{ color: "text.secondary" }}
        >
          Cómo se lee este reporte
        </Typography>

        <Stack marginTop={0.5}>
          <Typography fontSize={chico} textAlign="left">
            <span style={{ fontWeight: "bold" }}>Monto en mora: </span>
            lo que ya venció y no se pagó. <span style={{ fontWeight: "bold" }}>% Mora</span>{" "}
            es esa cifra sobre el saldo del barrio, y es la que hace comparables barrios de
            tamaños distintos: C$50,000 sin cobrar no significan lo mismo en un barrio con
            un millón colocado que en uno con ciento cincuenta mil.
          </Typography>
          <Typography fontSize={chico} textAlign="left" marginTop={0.5}>
            <span style={{ color: colorDe(MORA_ALTA), fontWeight: "bold" }}>
              {`Rojo: ${MORA_ALTA}% o más. `}
            </span>
            <span style={{ color: colorDe(MORA_MEDIA), fontWeight: "bold" }}>
              {`Naranja: ${MORA_MEDIA}% a ${MORA_ALTA}%. `}
            </span>
            <span style={{ color: colorDe(0), fontWeight: "bold" }}>
              {`Verde: menos de ${MORA_MEDIA}%.`}
            </span>
          </Typography>
          <Typography fontSize={chico} textAlign="left" marginTop={0.5}>
            <span style={{ fontWeight: "bold" }}>El barrio </span>
            sale del que tiene cargado cada cliente, así que la tabla está completa desde
            siempre. <span style={{ fontWeight: "bold" }}>El mapa </span>
            en cambio ubica cada barrio en el centro de sus clientes con ubicación conocida:
            la columna "Ubicados" dice cuántos son, y con pocos ese punto se corre.
          </Typography>
        </Stack>
      </>
    );
  };

  return (
    <div>
      <Dialog fullScreen open={true}>
        <AppBar sx={{ position: "relative" }}>
          <Toolbar>
            <img loading="lazy" src={icon} alt="logo" style={{ height: 40 }} />
            <Typography
              sx={{ ml: 2, flex: 1, textAlign: "center" }}
              variant="h5"
              component="div"
            >
              {`${title} `}
            </Typography>
            <img loading="lazy" src={icon} alt="logo" style={{ height: 40 }} />
          </Toolbar>
        </AppBar>

        <Stack display="flex" justifyContent="center">
          <Stack
            direction={"row"}
            justifyContent="space-between"
            paddingLeft={5}
            paddingRight={5}
            marginTop={1}
            marginBottom={1}
          >
            <IconButton
              onClick={() => exportExcel()}
              sx={{ color: "primary.main" }}
            >
              <FontAwesomeIcon
                icon={faCloudArrowDown}
                style={{ fontSize: 22, width: 22 }}
              />
            </IconButton>

            <Stack>
              <Typography
                sx={{
                  color: "text.secondary",
                  textAlign: "center",
                  fontWeight: "bold",
                }}
              >
                {slogan}
              </Typography>
              <Typography
                sx={{
                  color: "text.secondary",
                  textAlign: "center",
                  fontWeight: "bold",
                }}
                variant="h6"
                component="div"
              >
                Reporte de Mora por Zona
              </Typography>
            </Stack>

            <ReactToPrint
              trigger={() => {
                return (
                  <IconButton sx={{ color: "info.main" }}>
                    <PrintRoundedIcon style={{ fontSize: 22, width: 22 }} />
                  </IconButton>
                );
              }}
              content={() => compRef.current}
              pageStyle={pageStyle}
            />
          </Stack>

          <Box sx={{ paddingX: "22px", textAlign: "center" }}>
            {parametros(false)}
          </Box>
        </Stack>

        <Box sx={{ paddingX: "22px", textAlign: "center" }}>
          <Divider sx={{ marginY: "14px" }} />

          {isEmpty(zonas) ? (
            <NoData />
          ) : (
            <>
              {totales(false)}
              <Divider sx={{ marginY: "14px" }} />

              <Tabs
                value={vista}
                onChange={(e, valor) => setVista(valor)}
                centered
                sx={{ marginBottom: 2 }}
              >
                <Tab
                  value="lista"
                  label="Vista en lista"
                  icon={<FontAwesomeIcon icon={faListOl} />}
                  iconPosition="start"
                  sx={{ fontSize: 15, fontWeight: "bold" }}
                />
                <Tab
                  value="mapa"
                  label="Vista en mapa"
                  icon={<FontAwesomeIcon icon={faMapLocationDot} />}
                  iconPosition="start"
                  sx={{ fontSize: 15, fontWeight: "bold" }}
                />
              </Tabs>

              {vista === "lista" ? (
                tabla(false)
              ) : isEmpty(enMapa) ? (
                <Typography
                  fontSize={17}
                  color="#ed6c02"
                  marginY={3}
                  sx={{ maxWidth: 760, marginX: "auto", lineHeight: 1.5 }}
                >
                  Ningún barrio tiene todavía clientes con ubicación conocida, así que no
                  hay nada que dibujar. La tabla en cambio está completa: se arma con el
                  barrio que ya trae cada cliente.
                </Typography>
              ) : (
                <MapContainer
                  center={isEmpty(posiciones) ? CENTRO_POR_DEFECTO : posiciones[0]}
                  zoom={13}
                  style={{ height: "70vh", width: "100%", borderRadius: 15 }}
                  scrollWheelZoom
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />

                  <AjustarVista posiciones={posiciones} senal={enMapa.length} />

                  {enMapa.map((z) => (
                    <Marker
                      key={z.barrio}
                      position={[z.latitude, z.longitude]}
                      icon={icono(colorDe(z.porcentajeMora))}
                    >
                      <EtiquetaMapa permanent direction="right" offset={[10, 0]}>
                        {`${z.barrio} · ${z.porcentajeMora}%`}
                      </EtiquetaMapa>
                      <Popup>
                        <strong style={{ color: colorDe(z.porcentajeMora) }}>
                          {z.barrio}
                        </strong>
                        <br />
                        {`${z.creditos} créditos, ${z.creditosEnMora} en mora`}
                        <br />
                        {`Saldo ${moneda(z.saldoTotal)}`}
                        <br />
                        {`En mora ${moneda(z.montoEnMora)} (${z.porcentajeMora}%)`}
                        <br />
                        <small>
                          {`Ubicado con ${z.conUbicacion} de ${z.creditos} clientes`}
                        </small>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              )}

              {leyenda(false)}
            </>
          )}
        </Box>
      </Dialog>

      {/* En papel siempre la tabla: un mapa de puntos de colores en tonos grises no
      distingue nada. */}
      <div style={{ display: "none" }}>
        <PrintReport ref={compRef} titulo={"Reporte de Mora por Zona"}>
          <Container maxWidth={false} sx={{ textAlign: "center" }}>
            {parametros(true)}
            <Divider sx={{ marginY: "10px" }} />

            {totales(true)}
            <Divider sx={{ marginY: "10px" }} />

            {tabla(true)}
            {leyenda(true)}
          </Container>
        </PrintReport>
      </div>
    </div>
  );
};

export default MoraPorZona;
